import {
  Newspaper,
  Target,
  Crosshair,
  Cpu,
  Presentation,
  Database,
} from "lucide-react";

export const SOLUTIONS = [
  {
    id: "content-syndication",
    num: "01",
    icon: Newspaper,
    title: "Content Syndication",
    tag: "Targeted Content Syndication",
    headline: "Amplify Your Content. Engage Active Tech Buyers at Scale.",
    short:
      "Distribute whitepapers, analyst reports, buyer guides, and case studies to pre-profiled enterprise buyers inside contextually relevant digital environments.",
    desc: "You invest heavily in thought leadership, whitepapers, eBooks, benchmark reports, and case studies. But even the most compelling content fails to drive pipeline if it remains trapped behind your website's form. Vyacomm Media syndicates your high-value digital assets directly into the hands of pre-profiled B2B executives across targeted job roles, company sizes, and industries — guaranteeing qualified engagement.",
    bullets: [
      { t: "Guaranteed ICP Alignment", d: "Filter downloads by job function, seniority level, industry vertical, employee size, and annual revenue." },
      { t: "Contextual Relevance", d: "Assets hosted in authoritative environments where IT and enterprise leaders research tech trends." },
      { t: "Multi-Touch Follow-Up", d: "Pair digital downloads with secondary telephonic or email follow-ups to transition awareness into consideration." },
      { t: "Full Contact Telemetry", d: "Verified name, direct work email, phone, company, address, job title, and explicit opt-in confirmation." },
    ],
    specs: ["MQL Opt-Ins", "ICP Filters", "Asset Hosting", "Telemetry Reports"],
    big: true,
  },
  {
    id: "qualified-leads",
    num: "02",
    icon: Target,
    title: "Qualified Leads",
    tag: "MQL · HQL · BANT · CCL",
    headline: "From Engaged Readers to Confirmed Sales Conversations.",
    short:
      "Eliminate low-intent forms. Receive leads validated against strict demographic, firmographic, and custom discovery questions.",
    desc: "Not all leads are created equal. Feeding generic, low-intent inquiries to enterprise sales representatives wastes expensive sales cycles and demoralizes your revenue team. Vyacomm Media engineers tiered lead qualification programs customized to your exact pipeline requirements — from opt-in MQLs to BANT-confirmed opportunities and calendarized Confirmed Call-Backs.",
    bullets: [
      { t: "Marketing Qualified Lead (MQL)", d: "Verified target contact who opts in to strategic assets and confirms category interest — ideal for nurture streams." },
      { t: "High-Quality Lead (HQL)", d: "Prospect meets strict ICP parameters, engages with multiple assets, and answers custom discovery questions." },
      { t: "BANT Qualified Lead", d: "Confirmed Need, Budget, Authority, and a defined 3–12 month Timeline. Direct AE pipeline handoff." },
      { t: "Confirmed Call-Back (CCL)", d: "Decision-maker explicitly requests an exploratory call at a mutually agreed date and time." },
    ],
    specs: ["BANT Verified", "Custom Discovery Qs", "CCL Scheduling", "100% Replacement"],
    big: true,
  },
  {
    id: "account-based-marketing",
    num: "03",
    icon: Crosshair,
    title: "Account-Based Marketing",
    tag: "ABM Execution",
    headline: "Precision Account Penetration for High-Value Enterprise Deals.",
    short:
      "Activate your Target Account Lists. We map buying committees, reach multiple stakeholders, and drive multi-touch consensus.",
    desc: "When your growth hinges on closing Fortune 500, Tier-1 enterprise, or strategic mid-market accounts, spray-and-pray marketing is obsolete. Vyacomm Media's ABM engine turns your Target Account List (TAL) into active commercial relationships — mapping entire buying committees and striking when intent spikes.",
    bullets: [
      { t: "Buying Committee Mapping", d: "Engage technical champions, procurement, financial gatekeepers, and executive sponsors." },
      { t: "100% Target Account Matching", d: "Upload named accounts or let us build a high-probability TAL from historical win patterns." },
      { t: "Intent-Triggered Activation", d: "Account-level intent spikes tracked across thousands of external digital topics." },
      { t: "Custom Content Personalization", d: "Account-specific messaging and personalized asset syndication for enterprise consensus." },
    ],
    specs: ["TAL Matching", "Committee Mapping", "Intent Signals", "1:1 Personalization"],
    big: false,
  },
  {
    id: "technographic-targeting",
    num: "04",
    icon: Cpu,
    title: "Technographic Intelligence",
    tag: "Install-Base Targeting",
    headline: "Target Companies by the Technology Stack They Run Today.",
    short:
      "Pinpoint accounts running competing hardware, legacy software, or complementary cloud stacks for displacement campaigns.",
    desc: "Selling modern cloud infrastructure to an organization locked into a newly signed 5-year legacy contract is futile. Selling it to a company whose licensing expires in six months is an immediate win. We track 15,000+ technology install bases and cross-reference them with 140M+ executive profiles to power hyper-targeted campaigns.",
    bullets: [
      { t: "Competitive Displacement", d: "Reach competitor users with ROI calculators, transition roadmaps, and comparison guides." },
      { t: "Complementary Ecosystems", d: "Target users of platforms that integrate with yours — Salesforce, AWS, ServiceNow, Azure." },
      { t: "Firmographic Layering", d: "Combine install data with revenue, employee count, headquarters, and hiring trends." },
    ],
    specs: ["15,000+ Install Bases", "Displacement Plays", "Stack Filtering"],
    big: false,
  },
  {
    id: "webinar-lead-generation",
    num: "05",
    icon: Presentation,
    title: "Webinar & Virtual Events",
    tag: "Audience Acquisition",
    headline: "Pack Your Virtual Events with Pre-Qualified Enterprise Buyers.",
    short:
      "Guaranteed registration and attendee volumes for webinars, roundtables, and product demo sessions.",
    desc: "Webinars and virtual roundtables are among the highest-converting B2B channels, yet marketing teams routinely struggle with low registrations and disappointing show-up rates. We deliver guaranteed, ICP-matched registrant volume — and engineer the nurture cadence that gets them to attend live.",
    bullets: [
      { t: "Guaranteed Registration Quotas", d: "Pre-agreed registrant volumes by title, industry, and enterprise size." },
      { t: "Pre-Event Nurture Cadence", d: "Calendar invites, reminders, and executive briefings to maximize live attendance." },
      { t: "Post-Event Profiling", d: "Attendance records, question submissions, poll answers, and follow-up data delivered." },
    ],
    specs: ["Registrant Quotas", "Attendance Nurture", "Engagement Data"],
    big: false,
  },
  {
    id: "b2b-data-services",
    num: "06",
    icon: Database,
    title: "Custom B2B Data",
    tag: "Cleansing · Enrichment · TAM",
    headline: "Fuel Your RevOps Engine with Clean, Verified Contact Intelligence.",
    short:
      "Enrich, clean, and expand CRM records with direct dials, verified emails, and active intent indicators.",
    desc: "B2B data decays at over 30% per year as professionals switch roles, companies reorganize, and businesses merge. Operating on stale CRM data wastes SDR hours, damages deliverability, and inflates acquisition costs. We scrub, enrich, and custom-build audience universes for your exact go-to-market motion.",
    bullets: [
      { t: "Data Cleansing & Verification", d: "Remove invalid emails, update changed titles, and flag departed personnel." },
      { t: "Enrichment & Append", d: "Append direct dials, verified emails, LinkedIn URLs, revenue, and technology stacks." },
      { t: "TAM Sizing & Sourcing", d: "Bespoke databases for geographic expansion, vertical entry, or tier-based outbound." },
    ],
    specs: ["CRM Scrubbing", "Field Append", "TAM Mapping"],
    big: false,
  },
];

export const INDUSTRIES = [
  {
    name: "Enterprise Software & SaaS",
    desc: "From horizontal enterprise ERP platforms to vertical SaaS solutions, we connect software vendors with C-suite executives, VP-level department heads, and operational managers who prioritize efficiency, automation, and measurable software ROI.",
    personas: ["CIOs", "CTOs", "VPs of Business Applications", "Enterprise Architects", "Product Operations Heads"],
  },
  {
    name: "Cybersecurity & Risk Management",
    desc: "Cybersecurity buyers are notoriously protective of their time and contact information. Our established trust and educational publication channels allow security vendors to engage discerning infosec leaders in meaningful discussions around threat defense and regulatory compliance.",
    personas: ["CISOs", "VPs of Cyber Risk", "SecOps Directors", "Compliance Officers", "Network Security Engineers"],
  },
  {
    name: "Cloud Infrastructure, DevOps & Data Centers",
    desc: "Whether you offer hybrid cloud architecture, Kubernetes orchestration, storage systems, or colocation data centers, we reach the technical decision-makers tasked with maintaining agility, scalability, and system resilience.",
    personas: ["VPs of Cloud Infrastructure", "DevOps Leads", "Systems Engineering Directors", "Data Center Executives", "Chief Architect Officers"],
  },
  {
    name: "AI, Data Science & Analytics",
    desc: "As enterprise adoption of generative AI, LLM operations, and advanced data lakes explodes, Vyacomm Media helps AI vendors position their models and tooling in front of key technological innovators.",
    personas: ["Chief Data Officers", "Heads of AI/ML", "Data Engineering Leads", "VPs of Business Intelligence", "Chief Analytics Officers"],
  },
  {
    name: "FinTech, Banking & InsurTech",
    desc: "We deliver high-compliance, high-precision demand generation for financial technology providers offering payment gateways, fraud detection, core banking platforms, and automated underwriting engines.",
    personas: ["Chief Risk Officers", "VPs of Digital Banking", "CFOs", "Heads of Compliance & Fraud Prevention"],
  },
  {
    name: "HR Tech, Workforce & Enterprise EdTech",
    desc: "Empower HR technology providers to connect with talent leaders navigating remote work, employee experience, retention, and benefits administration.",
    personas: ["CHROs", "VPs of People Operations", "Heads of Talent Acquisition", "VPs of Total Rewards"],
  },
];

export const METRICS = [
  { value: 140, suffix: "M+", label: "Verified B2B Decision-Maker Contacts" },
  { value: 90, suffix: "+", label: "Global Countries Actively Covered" },
  { value: 15, suffix: "K+", label: "Hardware & Software Install Bases Tracked" },
  { value: 650, suffix: "K+", label: "Qualified Leads Delivered Annually" },
  { value: 99.4, suffix: "%", decimals: 1, label: "Campaign Allocation Fulfillment Rate" },
  { value: 100, suffix: "%", label: "GDPR, CCPA & CASL Compliance Assurance" },
];

export const TESTIMONIALS = [
  {
    quote:
      "Vyacomm Media is our most reliable lead publisher. When running high-stakes enterprise ABM campaigns for our Tier-1 cloud clients, they are the only partner that consistently hits 100% allocation without a single lead rejection. Their communication and proactive campaign optimization set the industry benchmark.",
    name: "Senior Media Director",
    org: "Global B2B Agency — New York",
    tag: "AGENCY PARTNER",
  },
  {
    quote:
      "Our sales team was tired of low-quality webinar downloads that never picked up the phone. Vyacomm Media shifted our program to custom BANT and Confirmed Call-Back leads. Within 90 days, our opportunity pipeline velocity increased by 42% and customer acquisition costs dropped significantly.",
    name: "VP of Growth Marketing",
    org: "Enterprise Cybersecurity SaaS — San Francisco",
    tag: "SAAS BRAND",
  },
  {
    quote:
      "Expanding across the DACH and Nordic markets was challenging until we deployed Vyacomm Media's localized content syndication engine. They delivered fully compliant, opt-in enterprise IT leads with native language validation. An outstanding growth partner.",
    name: "Regional Marketing Head",
    org: "Enterprise Infrastructure Solutions — London",
    tag: "EMEA ENTERPRISE",
  },
];
